// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: an event or mail draft the Assistant just made, as a card with an Open
// button. The event opens in Konvo's event window and the draft in Konvo's
// composer, so nobody is sent to Outlook to look at what was done.

import styles from '@app/features/konvo_assistant/components/AssistantItemCard.module.css';
import {openKonvoItem} from '@app/features/konvo_assistant/utils/KonvoItemLinks';
import {OutlookCalendarLogo} from '@app/features/ui/components/icons/OutlookCalendarLogo';
import {OutlookLogo} from '@app/features/ui/components/icons/OutlookLogo';
import type {AssistantItemBlock} from '@konvo/schema/src/domains/konvo/AssistantResponse';
import {useLingui} from '@lingui/react/macro';

/** "Thu 8 Oct · 14:00–15:00" in the reader's locale; all-day events are dates, not instants. */
export function itemWhen(block: AssistantItemBlock, locale?: string): string {
	const start = block.start ? new Date(block.start) : null;
	if (!start || Number.isNaN(start.getTime())) return '';
	const end = block.end ? new Date(block.end) : null;
	const zone = block.all_day ? {timeZone: 'UTC'} : {};
	const day = new Intl.DateTimeFormat(locale, {weekday: 'short', day: 'numeric', month: 'short', ...zone}).format(
		start,
	);
	if (block.all_day) return day;
	const time = new Intl.DateTimeFormat(locale, {hour: '2-digit', minute: '2-digit'});
	const range = end && !Number.isNaN(end.getTime()) ? `${time.format(start)}–${time.format(end)}` : time.format(start);
	return `${day} · ${range}`;
}

export function AssistantItemCard({block}: {block: AssistantItemBlock}) {
	const {t, i18n} = useLingui();
	const event = block.kind === 'event';
	const people = (block.people ?? []).join(', ');
	const cc = (block.cc ?? []).join(', ');
	const when = event ? itemWhen(block, i18n.locale) : '';
	const status = event
		? block.updated
			? t`Event updated`
			: t`Invites sent`
		: block.in_thread
			? t`Reply in the thread · not sent`
			: t`Draft · not sent`;
	const open = () => openKonvoItem({kind: block.kind, id: block.id});
	return (
		<section className={styles.card} data-flx="konvo-assistant.item-card">
			<span className={styles.mark} aria-hidden="true">
				{event ? <OutlookCalendarLogo size={20} /> : <OutlookLogo size={20} />}
			</span>
			<div className={styles.text}>
				<span className={styles.status}>{status}</span>
				<strong className={styles.title}>{block.title}</strong>
				{event && (when || block.location) ? (
					<span className={styles.meta}>{[when, block.location].filter(Boolean).join(' · ')}</span>
				) : null}
				{people ? <span className={styles.meta}>{event ? t`Invited: ${people}` : t`To: ${people}`}</span> : null}
				{cc ? <span className={styles.meta}>{t`Cc: ${cc}`}</span> : null}
				{block.snippet ? <p className={styles.snippet}>{block.snippet}</p> : null}
				<div className={styles.actions}>
					<button type="button" className={styles.openButton} onClick={open} data-flx="konvo-assistant.item-card.open">
						{event ? t`Open event` : t`Open draft`}
					</button>
				</div>
			</div>
		</section>
	);
}
